import * as React from 'react'
import { useMemo } from 'react'
import TaskList, { TaskListProps, TaskListSettings } from '../../components/List/Task/TaskList'
import { IActionButton } from '../../components/ActionButtons'
import { TaskAction, TaskStatus } from '../../config/taskConfig'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../reducers'
import ActionHeader from '../../components/Render/ActionHeader'
import { useMount } from '@fluentui/react-hooks'
import useUser from '../../hooks/useUser'
import UserPicker from '../../components/TagPicker/UserPicker'
import { useTranslation } from 'react-i18next'
import { Field } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../hooks/useViewport'
import TaskStatusPicker from '../../components/TagPicker/TaskStatusPicker'

export type TaskOverviewProps = TaskListProps & {
    setDefaultAssignee?: boolean
    setDefaultAssigner?: boolean
}

const updateSettings = (settings: Partial<TaskListSettings>) => ({
    type: TaskAction.OverviewSettings,
    payload: settings,
})

export default function TaskOverview(props: TaskOverviewProps) {
    const currentUser = useUser()
    const settings = useSelector<Store, TaskListSettings>(state => state.tasks.overview.settings)
    const dispatch = useDispatch()
    const { t: translate } = useTranslation()

    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('<', Viewport.XL) ? 'vertical' : 'horizontal'), [viewport])

    useMount(() => {
        dispatch(
            updateSettings({
                assignees: settings.assignees ?? (props.setDefaultAssignee ? [currentUser.id] : []),
                assigners: settings.assigners ?? (props.setDefaultAssigner ? [currentUser.id] : []),
            }),
        )
    })

    const actionButtonItems: IActionButton[] = useMemo(
        () => [
            {
                key: 'add-task',
                title: translate('action.create.task.title'),
                secondaryText: translate('action.create.task.subtitle'),
                iconName: 'TaskLogo',
                route: `${props.baseUrl}/add`,
            },
        ],
        [props.baseUrl],
    )

    return (
        <>
            <ActionHeader actionButtonItems={actionButtonItems}>
                <Field label={translate('task.filter.assignee')} orientation={orientation}>
                    <UserPicker
                        selectedOptions={settings.assignees || []}
                        onOptionsSelect={(_, selectedOptions) =>
                            dispatch(updateSettings({ assignees: selectedOptions }))
                        }
                    />
                </Field>
                <Field label={translate('task.filter.assigner')} orientation={orientation}>
                    <UserPicker
                        selectedOptions={settings.assigners || []}
                        onOptionsSelect={(_, selectedOptions) =>
                            dispatch(updateSettings({ assigners: selectedOptions }))
                        }
                    />
                </Field>
                <Field label={translate('task.status.label')} orientation={orientation}>
                    <TaskStatusPicker
                        selectedOptions={settings.statuses || []}
                        onOptionsSelect={(_, selectedOptions) =>
                            dispatch(updateSettings({ statuses: selectedOptions as TaskStatus[] }))
                        }
                    />
                </Field>
            </ActionHeader>
            <TaskList {...props} settings={settings} />
        </>
    )
}
